import { store } from './store'
import { api_url } from './runtime'

// Wrapper central de fetch com timeout DURO. Nenhum request do app pode ficar
// pendente para sempre — foi o que reprovou o build 6 na Apple (2.1a): um fetch
// abriu conexao mas a resposta nunca chegou (rede corporativa/proxy do revisor,
// ou task ECS fria) e o spinner do login girou infinitamente.
//
// CUIDADO com o CapacitorHttp: o fetch patcheado no iOS pode NAO honrar o
// AbortSignal. Por isso nao confiamos so no AbortController — corremos o fetch
// contra um timer que REJEITA. O race garante que o await SEMPRE termina (sucesso
// OU erro visivel) mesmo que o abort seja ignorado nativamente. Quando o abort
// funciona, ele dispara primeiro (em timeout_ms) e a rejeicao cai como transiente
// (is_transient_fetch_error casa /aborted/); quando e ignorado, o timer de
// timeout_ms+250 rejeita com "timeout: <path>" e vira erro visivel com retry.
export async function api_fetch(path: string, init?: RequestInit, timeout_ms = 12000): Promise<Response> {
  const controller = new AbortController()
  const abort_timer = setTimeout(() => controller.abort(), timeout_ms)
  let race_timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      fetch(api_url(path), { ...init, signal: controller.signal }),
      new Promise<Response>((_, reject) => {
        race_timer = setTimeout(() => reject(new Error('timeout: ' + path)), timeout_ms + 250)
      }),
    ])
  } finally {
    clearTimeout(abort_timer)
    if (race_timer !== undefined) clearTimeout(race_timer)
  }
}

// --- boundary helpers: res.json() é Promise<any> no lib DOM; tipar aqui evita
// que o `any` vaze pra todo o app (no-unsafe-*). Consumidores recebem tipo real
// quando conhecido, ou `unknown`/`unknown[]` (força narrowing honesto). ---

function error_of(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const message = (body as { error?: unknown }).error
    if (typeof message === 'string' && message) return message
  }
  return fallback
}

async function read_json<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

async function read_error(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => null)) as unknown
  return error_of(body, fallback)
}

async function response_error(res: Response, fallback: string): Promise<Error> {
  const body = (await res.json().catch(() => null)) as unknown
  const error_code = body && typeof body === 'object' && 'error_code' in body
    ? (body as { error_code?: unknown }).error_code
    : undefined
  return Object.assign(new Error(error_of(body, fallback)), {
    status: res.status,
    ...(typeof error_code === 'string' ? { error_code } : {}),
  })
}

function get_auth_headers(): Record<string, string> {
  const token = store.get('auth_token')
  if (!token) return {}
  return { Authorization: `Bearer ${token}` }
}

function idempotency_key(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `voucher-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function is_transient_fetch_error(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  return /Failed to fetch|Load failed|NetworkError|network connection was lost|cancelled|aborted/i.test(message)
}

async function post_voucher_request(path: string, body: Record<string, unknown>): Promise<Response> {
  const key = idempotency_key()
  const payload = { ...body, idempotency_key: key }
  const request = () => api_fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...get_auth_headers() },
    body: JSON.stringify(payload),
  })

  try {
    return await request()
  } catch (err) {
    if (!is_transient_fetch_error(err)) throw err
    console.warn('[voucher] falha de rede ao gerar voucher, tentando novamente:', err)
    await sleep(650)
    try {
      return await request()
    } catch (retryErr) {
      if (!is_transient_fetch_error(retryErr)) throw retryErr
      throw new Error('Não foi possível confirmar o voucher agora. Verifique sua conexão e tente novamente.', { cause: retryErr })
    }
  }
}

export type TelaInicialResponse = {
  logotipo?: string | null
  apelido?: string | null
  mensagem?: string | null
}

export async function fetch_tela_inicial(cnpj: string): Promise<TelaInicialResponse | null> {
  const params = new URLSearchParams({ cnpj })
  const res = await api_fetch(`/api/tela-inicial?${params}`)
  if (!res.ok) return null
  return read_json<TelaInicialResponse>(res)
}

export async function fetch_combustiveis(cnpj: string, phone: string): Promise<Record<string, unknown>[]> {
  const params = new URLSearchParams({ cnpj, phone })
  const res = await api_fetch(`/api/combustiveis?${params}`, {
    headers: get_auth_headers(),
  })
  if (!res.ok) throw await response_error(res, 'Erro ao buscar combustiveis')
  const data = await read_json<unknown>(res)
  const list: Record<string, unknown>[] = Array.isArray(data) ? (data as Record<string, unknown>[]) : []
  return list
}

export async function fetch_pagamentos(cnpj: string, phone: string, cod_produto: string): Promise<Record<string, unknown>[]> {
  const params = new URLSearchParams({ cnpj, phone, cod_produto })
  const res = await api_fetch(`/api/pagamentos?${params}`, {
    headers: get_auth_headers(),
  })
  if (!res.ok) throw await response_error(res, 'Erro ao buscar pagamentos')
  const data = await read_json<unknown>(res)
  const list: Record<string, unknown>[] = Array.isArray(data) ? (data as Record<string, unknown>[]) : []
  return list
}

export async function create_voucher(cnpj: string, phone: string, cod_produto: number, id_forma_pgto: number): Promise<Record<string, unknown>> {
  const res = await post_voucher_request('/api/voucher', { cnpj, phone, cod_produto, id_forma_pgto })
  if (!res.ok) throw await response_error(res, 'Erro ao gerar voucher')
  return read_json<Record<string, unknown>>(res)
}

export type AuthorizeInput = {
  recaptcha_token?: string
  geo?: { lat: number; lng: number }
  phone?: string
  cnpj?: string | null
  flow?: 'driver' | 'client' | 'origin-validation'
  return_path?: string
}

export type AuthorizeResult = {
  authorized: boolean
  auth_token?: string
  source?: string
  phone?: string
  cnpj?: string
  flow?: string
  error?: string
  challenge_required?: boolean
  reason?: string
  allow_browser_location?: boolean
  validation_url?: string
  error_code?: string
  retryable?: boolean
}

const AUTHORIZE_RETRY_DELAYS_MS = [500, 1_500] as const
const AUTHORIZE_UNAVAILABLE: AuthorizeResult = {
  authorized: false,
  error: 'Serviço de validação temporariamente indisponível. Tente novamente em instantes.',
  error_code: 'authorize_unavailable',
  retryable: true,
}

function authorize_result(body: unknown): AuthorizeResult {
  if (body && typeof body === 'object' && 'authorized' in body && typeof (body as { authorized?: unknown }).authorized === 'boolean') {
    return body as AuthorizeResult
  }
  return AUTHORIZE_UNAVAILABLE
}

function retryable_authorize_status(status: number): boolean {
  return status === 502 || status === 503 || status === 504
}

export async function authorize(input: string | AuthorizeInput): Promise<AuthorizeResult> {
  const payload = typeof input === 'string' ? { recaptcha_token: input } : input
  const request = () => api_fetch('/api/authorize', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  for (let attempt = 0; attempt <= AUTHORIZE_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const res = await request()
      const result = authorize_result((await res.json().catch(() => null)) as unknown)
      const delay = AUTHORIZE_RETRY_DELAYS_MS[attempt]
      if (retryable_authorize_status(res.status) && delay !== undefined) {
        console.warn(`[authorize] servico temporariamente indisponivel; nova tentativa em ${delay}ms`)
        await sleep(delay)
        continue
      }
      return result
    } catch (err) {
      if (!is_transient_fetch_error(err)) throw err
      const delay = AUTHORIZE_RETRY_DELAYS_MS[attempt]
      if (delay === undefined) return AUTHORIZE_UNAVAILABLE
      console.warn(`[authorize] falha transitoria de rede; nova tentativa em ${delay}ms`)
      await sleep(delay)
    }
  }

  return AUTHORIZE_UNAVAILABLE
}

export async function verify_origin_challenge(state: string, recaptcha_token: string): Promise<{ ticket: string; return_url: string }> {
  const res = await api_fetch('/api/origin-challenge/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ state, recaptcha_token }),
  })
  if (!res.ok) throw new Error(await read_error(res, 'Não foi possível validar a origem'))
  return read_json<{ ticket: string; return_url: string }>(res)
}

export async function exchange_origin_ticket(ticket: string): Promise<AuthorizeResult> {
  const res = await api_fetch('/api/origin-ticket', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticket }),
  })
  return read_json<AuthorizeResult>(res)
}

export type VerifiedAuthResult = {
  valid: boolean
  phone?: string
  cnpj?: string
  flow?: 'client' | 'driver'
}

export async function verify_auth(token: string): Promise<VerifiedAuthResult> {
  const res = await api_fetch('/api/verify-auth', {
    headers: { Authorization: `Bearer ${token}` },
  })
  return read_json<VerifiedAuthResult>(res)
}

export type ClientSessionResult = {
  valid: boolean
  auth_token?: string
  phone?: string
  cnpj?: string
  flow?: 'client' | 'driver'
  reason?: 'cookie_missing' | 'cookie_invalid' | 'identity_invalid' | 'token_blocked' | 'flow_mismatch'
}

export async function fetch_client_session(flow: 'client' | 'driver'): Promise<ClientSessionResult> {
  const params = new URLSearchParams({ flow })
  const res = await api_fetch(`/api/client-session?${params}`, { credentials: 'include' }, 3000)
  if (!res.ok) return { valid: false }
  return read_json<ClientSessionResult>(res)
}

export async function persist_client_session(auth_token: string, flow: 'client' | 'driver'): Promise<boolean> {
  const res = await api_fetch('/api/client-session', {
    method: 'POST',
    credentials: 'include',
    headers: { Authorization: `Bearer ${auth_token}` },
  })
  if (!res.ok) return false
  const body = await read_json<{ persisted?: boolean; flow?: string }>(res)
  return body.persisted === true && body.flow === flow
}

export async function logout_client_session(flow: 'client' | 'driver'): Promise<void> {
  const params = new URLSearchParams({ flow })
  const res = await api_fetch(`/api/client-session/logout?${params}`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Não foi possível encerrar a sessão.')
}

export type PwaPinState = Partial<Record<
  | 'cnpj'
  | 'phone'
  | 'auth_token'
  | 'station_name'
  | 'station_location'
  | 'station_logo'
  | 'latitude'
  | 'longitude'
  | 'whatsapp_link'
  | 'voucher'
  | 'driver_phone'
  | 'driver_tipo_motorista'
  | 'driver_placa'
  | 'driver_autorizacao'
  | 'driver_veiculo'
  | 'driver_offer_product_code'
  | 'driver_offer_payment_id'
  | 'driver_voucher',
  unknown
>>

export interface PwaPinPayload {
  v: 1 | 2 | 3
  ts?: number
  route_path: string
  route_search: string
  state: PwaPinState
  replacement_token?: string
}

export async function create_pwa_pin(payload: PwaPinPayload): Promise<{ token: string; manifest_url: string }> {
  const res = await api_fetch('/api/pwa/pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await read_error(res, 'Erro ao preparar atalho'))
  return read_json<{ token: string; manifest_url: string }>(res)
}

export async function fetch_pwa_pin(token: string): Promise<PwaPinPayload | null> {
  const params = new URLSearchParams({ token })
  const res = await api_fetch(`/api/pwa/pin?${params}`)
  if (res.status === 204) return null
  if (!res.ok) throw new Error(await read_error(res, 'Erro ao restaurar atalho'))
  return read_json<PwaPinPayload>(res)
}

// --- Motorista de Aplicativo ---

export async function fetch_driver_home(): Promise<unknown> {
  const res = await api_fetch('/api/motorista/tela-inicial')
  if (!res.ok) return null
  return read_json<unknown>(res)
}

export async function fetch_driver_products(): Promise<Record<string, unknown>[]> {
  const res = await api_fetch('/api/motorista/produtos')
  if (!res.ok) throw new Error('Erro ao buscar produtos')
  return read_json<Record<string, unknown>[]>(res)
}

export async function fetch_driver_payment_methods(): Promise<Record<string, unknown>[]> {
  const res = await api_fetch('/api/motorista/formas-pagamento')
  if (!res.ok) throw new Error('Erro ao buscar formas de pagamento')
  return read_json<Record<string, unknown>[]>(res)
}

export type DriverActivation = {
  placa?: string | null
  autorizacao?: string | null
  veiculo?: string | null
}

export async function driver_activate(data: {
  phone: string; urlqrcode: string; placa?: string; autorizacao?: string; veiculo?: string; dataemissao?: string
}): Promise<DriverActivation> {
  const res = await api_fetch('/api/motorista/ativacao', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...get_auth_headers() },
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error(await read_error(res, 'Erro na ativacao'))
  return read_json<DriverActivation>(res)
}

// Exclusão de dados do motorista (App Store 5.1.1(v)).
// O telefone NÃO vai no corpo: o servidor usa o claim assinado do auth_token.
export async function driver_delete_data(): Promise<void> {
  const res = await api_fetch('/api/motorista/dados', {
    method: 'DELETE',
    headers: get_auth_headers(),
  })
  if (!res.ok) throw new Error(await read_error(res, 'Não foi possível excluir sua conta agora.'))
}

export type DriverOfferListing = { status: 'desativada'; total_postos: number } | null
export type DriverOffersResult = { ofertas: Record<string, unknown>[]; listagem: DriverOfferListing }

export async function fetch_driver_offers(phone: string, cod_produto: string, id_forma_pgto = '0', tipo_motorista = 1): Promise<DriverOffersResult> {
  const params = new URLSearchParams({ phone, cod_produto, id_forma_pgto, tipo_motorista: String(tipo_motorista), incluir_status: '1' })
  const res = await api_fetch(`/api/motorista/ofertas?${params}`, {
    headers: get_auth_headers(),
  })
  if (!res.ok) throw new Error(await read_error(res, 'Erro ao buscar postos'))
  const data = await read_json<unknown>(res)
  // Compatibilidade durante a atualização: servidores anteriores retornam array.
  if (Array.isArray(data)) return { ofertas: data as Record<string, unknown>[], listagem: null }
  if (!data || typeof data !== 'object' || !('ofertas' in data) || !Array.isArray(data.ofertas)) {
    throw new Error('Não foi possível interpretar a listagem de postos.')
  }
  const status = 'listagem' in data ? data.listagem : null
  const listagem: DriverOfferListing =
    !data.ofertas.length && status && typeof status === 'object' &&
    'status' in status && status.status === 'desativada' &&
    'total_postos' in status && typeof status.total_postos === 'number' &&
    Number.isSafeInteger(status.total_postos) && status.total_postos > 0
      ? { status: 'desativada', total_postos: status.total_postos }
      : null
  return { ofertas: data.ofertas as Record<string, unknown>[], listagem }
}

export async function create_driver_voucher(
  cnpj: string,
  phone: string,
  cod_produto: number,
  id_forma_pgto: number,
  tipo_motorista = 1,
): Promise<Record<string, unknown>> {
  const res = await post_voucher_request('/api/motorista/voucher', { cnpj, phone, cod_produto, id_forma_pgto, tipo_motorista })
  if (!res.ok) throw new Error(await read_error(res, 'Erro ao gerar voucher'))
  return read_json<Record<string, unknown>>(res)
}

export async function fetch_driver_recent_vouchers(phone: string, tipo_motorista = 1): Promise<Record<string, unknown>[]> {
  const params = new URLSearchParams({ phone, tipo_motorista: String(tipo_motorista) })
  const res = await api_fetch(`/api/motorista/vouchers?${params}`, {
    headers: get_auth_headers(),
  })
  if (!res.ok) throw new Error(await read_error(res, 'Erro ao buscar vouchers'))
  const data = await read_json<unknown>(res)
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : []
}

export type DriverReviewCriterion = {
  id_criterio: number
  chave: string
  nome: string
  descricao?: string | null
  icone: string
  ordem: number
  exibir_na_lista: boolean
  exigir_na_avaliacao: boolean
}

export type DriverReviewSummaryCriterion = {
  id_criterio: number
  chave: string
  nome: string
  icone: string
  ordem: number
  total_notas: number
  media: number | null
}

export type DriverReviewSummary = {
  cnpj_empresa: string
  total_avaliacoes: number
  media_geral: number | null
  criterios: DriverReviewSummaryCriterion[]
}

export type DriverReviewPending =
  | { pendente: false }
  | {
    pendente: true
    cnpj_empresa: string
    id_voucher: number | null
    origem_tipo: string
    origem_chave: string
    voucher: Record<string, unknown>
    criterios: DriverReviewCriterion[]
  }

export type DriverReviewFueling = {
  index: number
  cnpj_empresa: string
  id_voucher: number | null
  origem_tipo: string
  origem_chave: string
  data_utilizacao: string
  avaliado: boolean
  pode_avaliar: boolean
  id_avaliacao: number | null
  voucher: Record<string, unknown>
}

export async function fetch_driver_review_fuelings(phone: string, cnpjEmpresa?: string | null): Promise<DriverReviewFueling[]> {
  const params = new URLSearchParams({ phone })
  const cnpj = String(cnpjEmpresa || '').replace(/\D/g, '')
  if (cnpj) params.set('cnpj_empresa', cnpj)
  const res = await api_fetch(`/api/motorista/avaliacoes/listagem?${params}`, {
    headers: get_auth_headers(),
  })
  const body = (await res.json().catch(() => null)) as unknown
  if (!res.ok) throw new Error(error_of(body, 'Erro ao buscar abastecimentos'))
  return Array.isArray(body) ? body as DriverReviewFueling[] : []
}

export async function fetch_driver_review_criteria(cnpj: string): Promise<DriverReviewCriterion[]> {
  const params = new URLSearchParams({ cnpj })
  const res = await api_fetch(`/api/motorista/avaliacoes/criterios?${params}`, {
    headers: get_auth_headers(),
  })
  const body = (await res.json().catch(() => null)) as unknown
  if (!res.ok) throw new Error(error_of(body, 'Erro ao buscar critérios'))
  return Array.isArray(body) ? body as DriverReviewCriterion[] : []
}

export async function fetch_driver_review_summaries(cnpjs: string[]): Promise<DriverReviewSummary[]> {
  const uniqueCnpjs = Array.from(new Set(cnpjs.map(item => String(item || '').replace(/\D/g, '')).filter(Boolean)))
  if (!uniqueCnpjs.length) return []
  const params = new URLSearchParams({ cnpjs: uniqueCnpjs.join(',') })
  const res = await api_fetch(`/api/motorista/avaliacoes/resumo?${params}`, {
    headers: get_auth_headers(),
  })
  const body = (await res.json().catch(() => null)) as unknown
  if (!res.ok) throw new Error(error_of(body, 'Erro ao buscar avaliações'))
  return Array.isArray(body) ? body as DriverReviewSummary[] : []
}

export async function fetch_driver_pending_review(phone: string, tipo_motorista = 1, cnpj?: string | null): Promise<DriverReviewPending> {
  const params = new URLSearchParams({ phone, tipo_motorista: String(tipo_motorista) })
  const directCnpj = String(cnpj || '').replace(/\D/g, '')
  if (directCnpj) params.set('direct_cnpj', directCnpj)
  const res = await api_fetch(`/api/motorista/avaliacoes/pendente?${params}`, {
    headers: get_auth_headers(),
  })
  const body = (await res.json().catch(() => null)) as unknown
  if (!res.ok) throw new Error(error_of(body, 'Erro ao buscar avaliação pendente'))
  return (body && typeof body === 'object' ? body : { pendente: false }) as DriverReviewPending
}

export async function create_driver_review(data: {
  cnpj: string
  phone: string
  tipo_motorista: number
  id_voucher?: number | null
  origem_tipo: string
  origem_chave: string
  comentario?: string
  notas: Array<{ id_criterio: number; nota: number }>
}): Promise<unknown> {
  const res = await api_fetch('/api/motorista/avaliacoes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...get_auth_headers() },
    body: JSON.stringify(data),
  })
  const body = (await res.json().catch(() => null)) as unknown
  if (!res.ok) throw new Error(error_of(body, 'Erro ao enviar avaliação'))
  return body
}

export interface GeoCepLookupResult {
  cep: string
  latitude: number
  longitude: number
  neighborhood: string
  city: string
  state: string
  address_count: number
  precision: string
  source: string
  display_name: string
}

export async function lookup_geo_cep(cep: string): Promise<GeoCepLookupResult> {
  const params = new URLSearchParams({ cep })
  const res = await api_fetch(`/api/geo/cep?${params}`, {
    headers: get_auth_headers(),
  })
  const body = (await res.json().catch(() => null)) as unknown
  if (!res.ok) throw new Error(error_of(body, 'Erro ao localizar CEP'))
  return body as GeoCepLookupResult
}
